/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { makeReactive } from '@dha/make-reactive';
import { styleForPrint, nextTick } from '@/services/helpers';
import JSPDFRenderer from './jsPDF';
import pdfStyle, { applyStyles, pageLayout } from './pdfStyle.config';
import loadContent from './PDFLoader';


const delayThrottle = 50;


// async function wait(n = 0, prom) {
//     return prom;
//     return new Promise(resolve => setTimeout(() => resolve(val), n));
// }
// eslint-disable-next-line
const testPage = {
    root: {
        page1: {
            debug: true,
            optional: true,
            newPage: false,
            test1: {
                // position: 'col-2',
                text: 'Testsdklfj'
            }
        }
    }
};

// eslint-disable-next-line
// const sampleModel = {
//     testRoot(n) {
//         console.log('Test root', n);
//         console.log('Hows this goin', this);
//     },
//     document: () => ({
//         test1: {
//             page: testPage
//         },
//         preface: {
//             page0: {
//                 text: 'Sections can have pages'
//             },
//             page1: {
//                 page: ({ num }) => ({
//                     newPage: true,
//                     content: {
//                         text: `This is  ${num} page`
//                     }
//                 })
//             },
//             optionalSection: {
//                 page: ({ num }) => ({
//                     text: `Hello This is  ${num} page`,
//                     position: 'col-2'
//                 })
//             }
//         },
//         ch1: {
//             page0: {
//                 text: 'Hello Ch1'
//             },
//             page1: {
//                 page: ({ num }) => ({
//                     text: `Ch1 Page1 Same page as ch title ${num}`
//                 })
//             }
//         },
//         test2: {
//             title: 'Test Page',
//             trythis: testPage
//         }
//     })
// };

// Base class for Document model sections
// Subclasses are Leafs or Containers.
class SectionNode {
    constructor(model, key) {
        this.model = model;
        this.id = key;
        this.print = (model.print !== undefined) ? model.print : true;
        this.showChildren = model.showChildren || false;
    }

    get title() {
        if (this.model.optional && this.optional !== true) {
            return this.model.optional;
        }
        return this.model.title || this.id;
    }

    get optional() {
        return this.model.optional;
    }

    get debug() {
        return this.model.debug;
    }

    get newPage() {
        return this.model.newPage;
    }
}

// Leaf Nodes are the ones actually rendered to doc.
class LeafNode extends SectionNode {
    constructor(model, key) {
        super(model, key);
        this.model = model;
        this.loading = false;
        this.loaded = false;
        this.creating = false;
        this.created = false;
        this.rendered = false;
        this.rendering = false;
    }

    reset() {
        // TODO: Make this smarter/reactive to the document
        // Should only need to reset if document has changed.
        this.rendered = false;
        this.rendering = false;
        this.loaded = false;
        this.loading = false;
        this.created = false;
        this.creating = false;
    }

    get sections() {
        return this.model.sections;
    }

    get isRendered() {
        return !!this.rendered;
    }

    get isRendering() {
        return !!this.rendering && !this.isRendered || this.isLoaded && !this.isRendered;
    }

    get isLoaded() {
        return !!this.loaded;
    }

    get isLoading() {
        return !!this.loading && !this.isLoaded;
    }

    get isCreated() {
        return !!this.created;
    }

    get isCreating() {
        return !!this.creating && !this.isCreated;
    }

    get isWorking() {
        return this.isLoading || this.isCreating || this.isRendering;
    }

    get status() {
        if (this.isRendered) {
            return 'Done';
        }
        if (this.isRendering) {
            return 'Rendering';
        }
        if (this.isCreating) {
            return 'Creating';
        }
        if (this.isLoading) {
            return 'Loading';
        }
        if (this.isCreated) {
            return 'Created';
        }
        return null;
    }

    // Apply styles and loadContent for page
    async load() {
        if (!this.created) {
            console.warn('Cannot load until after create()');
            return this.loading;
        }
        this.loading = loadContent(this.created);
        // Throttle for testing long loads
        this.loading = nextTick(this.num * delayThrottle * 2, this.loading);

        this.loaded = await this.loading;
        return this.loading;
    }

    // Creates the document node for rendering fromthe model
    async create(options) {
        // this.num = n;
        // Just a wrapper to perform the tasks async
        const createAsync = async () => {
            let nodeToCreate = this.model;
            if (typeof this.model === 'function') {
                nodeToCreate = this.model(this);
            }
            const clonedNode = _.cloneDeepWith(nodeToCreate,
                // eslint-disable-next-line
                (val) => {
                    if (typeof val === 'function') {
                        return val(this, options);
                    }
                });
            const delay = delayThrottle * (this.num || 1);
            await nextTick(delay);
            this.created = applyStyles(clonedNode);
            this.load();
            return this.created;
        };
        this.creating = createAsync();
        return this.creating;
    }
}

class ContainerNode extends SectionNode {
    constructor(model, key = 'root') {
        super(model, key);
        this.container = true;
        this.showChildren = (model.showChildren === undefined);
        // Remove properties that are not nodes
        const nodes = _.pickBy(model, (prop) => prop && (typeof prop === 'object' || typeof prop === 'function'));
        this.nodes = _.mapValues(nodes, (child, k) => {
            if (child.container) {
                return makeReactive(new ContainerNode(child, k));
            }
            if (k !== 'container') {
                return makeReactive(new LeafNode(child, k));
            }
            return child;
        });
        // console.log('ContainerNode', this.nodes);
    }

    // sections used by selector to choose printable
    get sections() {
        return this.nodes;
        // return _.pickBy(this.nodes, (node) => node.title !== this.title);
    }

    get leafs() {
        return _.pickBy(this.nodes, (node) => node instanceof LeafNode);
    }

    get containers() {
        return _.pickBy(this.nodes, (node) => node instanceof ContainerNode);
    }
}

class PageLayoutNode extends LeafNode {
    constructor() {
        super(pageLayout, 'pageLayout');
    }
}


function callProp(p) {
    if (typeof p === 'function') {
        return p();
    }
    return p;
}

// TODO: This should either just retrun ALL leafs, or print leafs.
// Right now, it is returning all leafs of print containers when printOnly is set
// Should not need this, the collate method should get all and then filter for print there.
function getAllLeafNodes(container) {
    const leafs = [];
    leafs.push(_.values(container.leafs));
    _.forEach(container.containers, (c) => {
        leafs.push(getAllLeafNodes(c));
    });
    return _.flatten(leafs);
}

function getAllNodes(container) {
    const nodes = [];
    nodes.push(_.values(container.nodes));
    _.forEach(container.nodes, (c) => {
        nodes.push(getAllNodes(c));
    });
    return _.flatten(nodes);
}

class PDFCreatorStatus {
    constructor(rootNode) {
        this.rootNode = rootNode;
    }

    get sections() {
        const sections = getAllLeafNodes(this.rootNode);
        return sections;
    }

    get working() {
        return !!_.find(this.sections, 'isWorking');
    }

    get message() {
        return this.working ? 'Working' : 'Ready';
    }

    get created() {
        return _.countBy(this.sections, (section) => section.isCreated).true || 0;
    }

    get loaded() {
        return _.countBy(this.sections, (section) => section.isLoaded).true || 0;
    }

    get rendered() {
        return _.countBy(this.sections, (section) => section.isRendered).true || 0;
    }

    get total() {
        return _.countBy(this.sections, (section) => section.print).true || 0;
    }
}


export default class PDFCreator {
    constructor(pdfDocument) {
        // this.pdfDocument = testPage;
        // console.log('TODO USe', pdfDocument);
        this.pdfDocument = pdfDocument;
        this.sectionsToPrint = null;
        this.createRootNode();
        this.pageLayoutNode = new PageLayoutNode();
        // this.status = makeReactive(new PDFCreatorStatus(this));
    }

    createRootNode() {
        // this.model.$watch('document', () => {
        const modelRoot = callProp(this.pdfDocument.root);
        this.rootNode = makeReactive(new ContainerNode(modelRoot));
        this.pageLayoutNode = new LeafNode(pageLayout, 'pageLayout', true);
        // });
    }

    init() {
        const { unit, format, baseFont } = pdfStyle;
        this.renderer = new JSPDFRenderer({ baseFont, unit, format });
        pdfStyle.use(this.renderer);
        // If the sections to print were created already, reset them
        if (this.sectionsToPrint) {
            _.forEach(this.sectionsToPrint, (section) => section.reset());
        }
        this.pdfDocument.reset();
    }

    get status() {
        return makeReactive(new PDFCreatorStatus(this.rootNode));
    }

    // Sets all sections to print for full document.
    set printAll(print = true) {
        _.each(this.allNodes, (node) => {
            node.print = print;
            if (node.sections) {
                const optionalSections = _.pickBy(node.sections, (subSection) => subSection.optional);
                _.each(optionalSections, (optionalSection) => {
                    optionalSection.print = print;
                });
            }
        });
    }

    // Checks all pages are printed AND all optional sections in each page printed
    get printAll() {
        return _.every(this.allNodes, (node) => {
            let printAllOptional = true;
            if (node.sections) {
                const optionalSections = _.pickBy(node.sections, (subSection) => subSection.optional);
                printAllOptional = _.every(optionalSections, 'print');
            }
            return node.print && printAllOptional;
        });
    }

    // Just checks if all pages are printed.
    // Optional content in a page only checked in printAll
    get printAllPages() {
        return _.every(this.allNodes, (node) => !node.newPage || node.newPage && node.print);
    }

    // Convenience to get all nodes recursively.
    get allNodes() {
        return getAllNodes(this.rootNode);
    }

    // Convenience to get all printable nodes recursively.
    get allPrintNodes() {
        return _.pickBy(getAllLeafNodes(this.rootNode), (node) => node.print);
    }

    // Convenience method, exposes the top level rootNode nodes as sections.
    get sections() {
        return this.rootNode.nodes;
    }

    async create() {
        await nextTick();
        await styleForPrint();
        await nextTick();
        this.sectionsToPrint = [];
        await this.createDocument();
        await this.renderDoc();
        styleForPrint(false);
        // await nextTick();
    }


    async createDocument() {
        this.pageNum = 0;
        this.collatePrintSections();
        for (let i = 0; i < this.sectionsToPrint.length; i++) {
            const node = this.sectionsToPrint[i];
            node.create(this);
        }
        await Promise.all(_.map(this.sectionsToPrint, 'creating'));
    }

    collatePrintSections() {
        _.forEach(this.allPrintNodes, (node) => {
            if (node.newPage) {
                this.pageNum++;
            }
            node.num = this.pageNum;
            this.sectionsToPrint.push(node);
            if (node.debug) {
                this.sectionsToPrint.push(this.pageLayoutNode);
            }
        });
    }

    async renderDoc() {
        // TODO: Wait for the promises in themodel instead of this array
        await Promise.all(_.map(this.sectionsToPrint, 'loading'));
        await nextTick();
        _.map(this.sectionsToPrint, async (sectionToPrint) => {
            // IMPORTANT: The actual renderer function MUST remain synchronous, or prints pages out of order
            const renderAsync = async () => {
                const rendered = this.renderer.page(sectionToPrint.loaded, sectionToPrint.newPage, this.pdfDocument.references.pageNumber);
                await nextTick(delayThrottle * sectionToPrint.num);
                return rendered;
            };
            sectionToPrint.rendering = renderAsync();
            sectionToPrint.rendered = await sectionToPrint.rendering;
        });
        await Promise.all(_.map(this.sectionsToPrint, 'rendering'));
    }

    async save(fileName) {
        const props = { ...this.pdfDocument.model.properties };
        // TODO: this.printAll needs to check optional subsections.
        if (!this.printAll) {
            props.subject = `${props.subject} - User Customized`;
            fileName = fileName.replace('_', '_Custom_');
        }
        props.creator = `${this.pdfDocument.model.version} at ${this.pdfDocument.model.clientInfo}`;
        await this.renderer.save(fileName, props);
    }
}

export function createPDFCreator(model) {
    return makeReactive(new PDFCreator(model));
}
